// Quiz Validation Utilities

export interface ValidationIssue {
  questionId?: string;
  type: 'TOPIC_MISMATCH' | 'SUBJECT_MISMATCH' | 'MISSING_ANSWER' | 'INVALID_ANSWER' | 'WRONG_SUBJECT';
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  message: string;
  question?: string;
  recommendation?: string;
}

export interface ValidationReport {
  passed: boolean;
  score: number;
  issues: ValidationIssue[];
  detectedSubject?: string;
  subjectMismatch?: boolean;
}

// Subject keyword mappings
const SUBJECT_KEYWORDS = {
  Chemistry: ['element', 'atom', 'molecule', 'compound', 'reaction', 'periodic', 'ion', 'bond', 'acid', 'base', 'chemical', 'electron', 'proton', 'neutron', 'isotope', 'solution'],
  Biology: ['cell', 'DNA', 'organism', 'gene', 'enzyme', 'protein', 'mitochondria', 'photosynthesis', 'cellular', 'membrane', 'nucleus', 'cytoplasm', 'chromosome', 'evolution', 'species'],
  Physics: ['force', 'energy', 'mass', 'velocity', 'momentum', 'gravity', 'acceleration', 'friction', 'motion', 'inertia', 'newton', 'joule', 'watt', 'pressure', 'density'],
  Mathematics: ['equation', 'variable', 'function', 'graph', 'calculate', 'solve', 'formula', 'algebra', 'geometry', 'fraction', 'derivative', 'integral', 'coefficient'],
  'Computer Science': ['algorithm', 'code', 'program', 'binary', 'data', 'array', 'loop', 'function', 'variable', 'debug', 'software', 'hardware'],
  History: ['war', 'empire', 'revolution', 'century', 'ancient', 'medieval', 'dynasty', 'civilization', 'treaty', 'colonial'],
  Geography: ['continent', 'ocean', 'climate', 'latitude', 'longitude', 'terrain', 'ecosystem', 'region', 'country', 'capital']
};

// Topic-to-subject mapping for common school topics
const TOPIC_TO_SUBJECT_MAP: Record<string, string> = {
  // Physics topics
  'states of matter': 'Physics',
  'matter': 'Physics',
  'forces': 'Physics',
  'force': 'Physics',
  'gravity': 'Physics',
  'energy': 'Physics',
  'motion': 'Physics',
  'electricity': 'Physics',
  'magnetism': 'Physics',
  'waves': 'Physics',
  'light': 'Physics',
  'sound': 'Physics',

  // Chemistry topics
  'elements': 'Chemistry',
  'atoms': 'Chemistry',
  'periodic table': 'Chemistry',
  'chemical reactions': 'Chemistry',
  'acids and bases': 'Chemistry',
  'compounds': 'Chemistry',
  'molecules': 'Chemistry',

  // Biology topics
  'cells': 'Biology',
  'photosynthesis': 'Biology',
  'dna': 'Biology',
  'evolution': 'Biology',
  'genetics': 'Biology',
  'mitosis': 'Biology',
  'osmosis': 'Biology',
  'plants': 'Biology',
  'animals': 'Biology',
  'ecosystems': 'Biology',

  // Earth Science topics
  'rocks': 'Earth Science',
  'water cycle': 'Earth Science',
  'weathering': 'Earth Science',
  'erosion': 'Earth Science',
  'climate change': 'Geography',
  'weather': 'Geography',
  'solar system': 'Science',

  // Math topics
  'fractions': 'Mathematics',
  'algebra': 'Mathematics',
  'geometry': 'Mathematics',
  'probability': 'Mathematics',
  'statistics': 'Mathematics'
};

// Topic-specific synonym mappings - expanded for better validation accuracy
const TOPIC_SYNONYMS: Record<string, string[]> = {
  // Chemistry topics
  'elements': ['element', 'atom', 'atomic', 'periodic', 'compound', 'chemical', 'proton', 'electron', 'neutron', 'isotope'],
  'acids and bases': ['acid', 'base', 'pH', 'alkaline', 'neutral', 'litmus', 'indicator', 'hydroxide', 'hydrogen ion'],
  'chemical reactions': ['reaction', 'reactant', 'product', 'catalyst', 'combustion', 'synthesis', 'decomposition', 'equation'],

  // Biology topics
  'cells': ['cell', 'cellular', 'membrane', 'nucleus', 'cytoplasm', 'organelle', 'mitochondria', 'chloroplast', 'ribosome'],
  'photosynthesis': ['chloroplast', 'glucose', 'oxygen', 'carbon dioxide', 'sunlight', 'plant', 'chlorophyll', 'light reaction'],
  'osmosis': ['osmotic', 'diffusion', 'membrane', 'concentration', 'water', 'solute', 'permeable', 'tonicity'],
  'mitosis': ['cell division', 'chromosome', 'prophase', 'metaphase', 'anaphase', 'telophase', 'spindle', 'cytokinesis'],
  'dna': ['DNA', 'gene', 'chromosome', 'nucleotide', 'base pair', 'replication', 'transcription', 'translation'],
  'evolution': ['natural selection', 'adaptation', 'species', 'darwin', 'mutation', 'fitness', 'survival'],

  // Physics topics
  'gravity': ['gravitational', 'force', 'mass', 'attraction', 'weight', 'newton', 'acceleration', 'fall', 'orbit'],
  'energy': ['kinetic', 'potential', 'thermal', 'electrical', 'mechanical', 'power', 'joule', 'work', 'heat'],
  'force': ['push', 'pull', 'newton', 'acceleration', 'mass', 'friction', 'tension', 'normal force'],
  'motion': ['velocity', 'acceleration', 'speed', 'displacement', 'trajectory', 'momentum', 'inertia'],
  'states of matter': ['solid', 'liquid', 'gas', 'plasma', 'phase', 'melting', 'freezing', 'boiling', 'condensation', 'evaporation', 'sublimation', 'deposition', 'particle', 'kinetic', 'temperature', 'pressure', 'volume', 'molecules', 'arrangement', 'density', 'ice', 'water', 'vapor', 'steam', 'energy'],
  'matter': ['solid', 'liquid', 'gas', 'plasma', 'phase', 'state', 'particle', 'mass', 'volume', 'density', 'properties'],
  'electricity': ['current', 'voltage', 'resistance', 'circuit', 'electron', 'charge', 'conductor', 'insulator', 'ampere', 'volt'],
  'waves': ['wavelength', 'frequency', 'amplitude', 'sound', 'light', 'electromagnetic', 'vibration', 'hertz'],

  // Math topics
  'fractions': ['fraction', 'numerator', 'denominator', 'divide', 'ratio', 'part', 'whole', 'simplify'],
  'algebra': ['variable', 'equation', 'solve', 'expression', 'coefficient', 'linear', 'quadratic', 'polynomial'],
  'geometry': ['shape', 'angle', 'triangle', 'circle', 'polygon', 'area', 'perimeter', 'volume', 'theorem'],
  'probability': ['chance', 'likelihood', 'outcome', 'event', 'random', 'sample', 'ratio', 'percent'],

  // Earth Science topics
  'water cycle': ['evaporation', 'condensation', 'precipitation', 'collection', 'rain', 'cloud', 'vapor', 'cycle'],
  'rocks': ['igneous', 'sedimentary', 'metamorphic', 'mineral', 'crystal', 'erosion', 'weathering', 'magma'],
  'weather': ['temperature', 'precipitation', 'humidity', 'wind', 'pressure', 'forecast', 'climate', 'atmosphere'],

  // General catch-all patterns
  'plants': ['plant', 'leaf', 'root', 'stem', 'flower', 'seed', 'photosynthesis', 'growth'],
  'animals': ['animal', 'mammal', 'bird', 'reptile', 'amphibian', 'species', 'habitat', 'adaptation'],
  'solar system': ['planet', 'sun', 'moon', 'orbit', 'asteroid', 'comet', 'galaxy', 'solar', 'space']
};

/**
 * Detects the most likely subject based on topic
 */
export function detectSubjectFromTopic(topic: string): string | null {
  const topicLower = topic.toLowerCase().trim();

  // Step 1: Check exact topic-to-subject mapping first
  for (const [topicKey, subjectValue] of Object.entries(TOPIC_TO_SUBJECT_MAP)) {
    if (topicLower.includes(topicKey.toLowerCase())) {
      return subjectValue;
    }
  }

  // Step 2: Fall back to regex keyword matching
  // Biology keywords (CHECK FIRST - more specific)
  if (/\b(cell|DNA|gene|organism|photosynthesis|respiration|mitosis|evolution|enzyme|protein|membrane|nucleus|chromosome|cellular|species|biodiversity|ecology|ecosystem)\b/.test(topicLower)) {
    return 'Biology';
  }

  // Chemistry keywords
  if (/\b(element|atom|compound|molecule|periodic|chemical reaction|acid|base|ion|bond|chemical|isotope)\b/.test(topicLower)) {
    return 'Chemistry';
  }

  // Physics keywords - EXPANDED to include matter/states/particles
  if (/force|gravity|motion|energy|mass|velocity|acceleration|momentum|friction|inertia|pressure|matter|particle|solid|liquid|gas|plasma/.test(topicLower)) {
    return 'Physics';
  }

  // Math keywords
  if (/equation|algebra|calculus|geometry|fraction|integer|variable|function|derivative|integral/.test(topicLower)) {
    return 'Mathematics';
  }

  // Computer Science keywords
  if (/algorithm|code|program|binary|software|hardware|debug|loop|array/.test(topicLower)) {
    return 'Computer Science';
  }

  // History keywords
  if (/war|empire|revolution|ancient|medieval|dynasty|civilization|treaty|colonial/.test(topicLower)) {
    return 'History';
  }

  // Geography keywords
  if (/continent|ocean|climate|latitude|longitude|terrain|ecosystem|country/.test(topicLower)) {
    return 'Geography';
  }

  return null; // Unknown subject
}

/**
 * Gets topic-specific keywords for validation
 */
function getTopicKeywords(topic: string): string[] {
  const topicLower = topic.toLowerCase();
  const keywords = TOPIC_SYNONYMS[topicLower] || [];

  // Also include the topic words themselves
  const topicWords = topic.toLowerCase().split(/\s+/).filter(w => w.length > 2);

  return [...keywords, ...topicWords];
}

/**
 * Checks if text contains multiple keywords from a list
 */
function hasMultipleKeywords(text: string, keywords: string[], minCount: number = 2): boolean {
  const textLower = text.toLowerCase();
  const matchCount = keywords.filter(kw => textLower.includes(kw)).length;
  return matchCount >= minCount;
}

/**
 * Checks if text contains any keyword from a list
 */
function hasAnyKeyword(text: string, keywords: string[]): boolean {
  const textLower = text.toLowerCase();
  return keywords.some(kw => textLower.includes(kw));
}

/**
 * Calculates topic relevance score (0-1)
 */
function checkTopicRelevance(questionText: string, topic: string): number {
  const questionLower = questionText.toLowerCase();
  const topicKeywords = getTopicKeywords(topic);

  if (topicKeywords.length === 0) return 0.5; // Neutral if no keywords

  const matchCount = topicKeywords.filter(kw => questionLower.includes(kw)).length;
  return Math.min(1, matchCount / Math.max(1, topicKeywords.length / 2));
}

/**
 * Validates generated questions
 */
export function validateGeneratedQuestions(
  questions: any[],
  topic: string,
  subject: string
): ValidationReport {
  const issues: ValidationIssue[] = [];

  // 1. Validate each question for topic relevance
  for (const q of questions) {
    const questionText = q.stem || q.text || '';
    const relevanceScore = checkTopicRelevance(questionText, topic);

    // Lowered threshold to 15% to reduce false positives
    if (relevanceScore < 0.15) { // Less than 15% relevant
      issues.push({
        questionId: q.id,
        type: 'TOPIC_MISMATCH',
        severity: 'HIGH',
        message: `Question doesn't seem related to topic "${topic}"`,
        question: questionText.substring(0, 80)
      });
    }
  }

  // 2. Check subject consistency (only if subject is not "General")
  if (subject !== 'General') {
    const subjectKeywords = SUBJECT_KEYWORDS[subject] || [];

    // Skip validation if subject has no keywords defined
    if (subjectKeywords.length > 0) {
      const questionsWithSubjectKeywords = questions.filter(q => {
        const text = q.stem || q.text || '';
        return hasAnyKeyword(text, subjectKeywords);
      });

      const subjectMatchPercentage = (questionsWithSubjectKeywords.length / questions.length) * 100;

      // Lowered threshold to 30% and made it MEDIUM severity (warning only)
      if (subjectMatchPercentage < 30) {
        issues.push({
          type: 'SUBJECT_MISMATCH',
          severity: 'MEDIUM', // Changed from HIGH to MEDIUM (warning only)
          message: `Only ${Math.round(subjectMatchPercentage)}% of questions contain ${subject} keywords`,
          recommendation: 'Questions may be about a specific topic within this subject or use different terminology'
        });
      }
    }
  }

  // 3. Check for cross-subject contamination
  const otherSubjects = Object.keys(SUBJECT_KEYWORDS).filter(s => s !== subject);

  for (const q of questions) {
    const questionText = q.stem || q.text || '';

    for (const otherSubject of otherSubjects) {
      const otherKeywords = SUBJECT_KEYWORDS[otherSubject] || [];

      if (hasMultipleKeywords(questionText, otherKeywords, 2)) {
        issues.push({
          questionId: q.id,
          type: 'WRONG_SUBJECT',
          severity: 'HIGH',
          message: `Question appears to be about ${otherSubject}, not ${subject}`,
          question: questionText.substring(0, 80)
        });
        break; // Only flag once per question
      }
    }
  }

  // 4. Validate answer keys
  for (const q of questions) {
    const hasCorrectIndex = q.correctIndex !== undefined && q.correctIndex !== null;
    const hasOptions = q.options && q.options.length > 0;

    if (!hasCorrectIndex && hasOptions) {
      issues.push({
        questionId: q.id,
        type: 'MISSING_ANSWER',
        severity: 'CRITICAL',
        message: 'Question has no correct answer marked'
      });
    }

    if (hasCorrectIndex && hasOptions && q.correctIndex >= q.options.length) {
      issues.push({
        questionId: q.id,
        type: 'INVALID_ANSWER',
        severity: 'CRITICAL',
        message: 'Correct answer index out of range'
      });
    }
  }

  // Calculate validation score
  const score = calculateValidationScore(questions, issues);

  // Check if subject was auto-detected differently
  const detectedSubject = detectSubjectFromTopic(topic);
  const subjectMismatch = detectedSubject && detectedSubject !== subject;

  return {
    passed: issues.filter(i => i.severity === 'CRITICAL').length === 0,
    score,
    issues,
    detectedSubject: detectedSubject || undefined,
    subjectMismatch
  };
}

/**
 * Calculates validation score (0-100)
 */
function calculateValidationScore(questions: any[], issues: ValidationIssue[]): number {
  let score = 100;

  for (const issue of issues) {
    switch (issue.severity) {
      case 'CRITICAL':
        score -= 25; // -25 points per critical issue
        break;
      case 'HIGH':
        score -= 15; // -15 points per high issue
        break;
      case 'MEDIUM':
        score -= 8; // -8 points per medium issue
        break;
      case 'LOW':
        score -= 3; // -3 points per low issue
        break;
    }
  }

  return Math.max(0, Math.min(100, score)); // Clamp between 0-100
}

/**
 * Pre-validate quiz request BEFORE calling OpenAI
 * This prevents wasted API calls on invalid/incomplete requests
 */
export function preValidateRequest(builderState: {
  title: string;
  count: number;
  questionTypes?: string[];
  subject?: string;
}): ValidationReport {
  const issues: ValidationIssue[] = [];

  // CRITICAL: Topic/title must not be empty
  if (!builderState.title || builderState.title.trim().length === 0) {
    issues.push({
      type: 'TOPIC_MISMATCH',
      severity: 'CRITICAL',
      message: 'Topic is empty. Please enter a specific topic for your quiz.',
      recommendation: 'Add a topic like "Photosynthesis" or "World War II"'
    });
  }

  // CRITICAL: Topic must be at least 3 characters
  if (builderState.title && builderState.title.trim().length < 3) {
    issues.push({
      type: 'TOPIC_MISMATCH',
      severity: 'CRITICAL',
      message: 'Topic is too short. Please use a more specific topic.',
      recommendation: 'Use at least 3 characters for better question quality'
    });
  }

  // HIGH: Question count must be reasonable
  if (builderState.count < 1 || builderState.count > 50) {
    issues.push({
      type: 'INVALID_ANSWER',
      severity: 'CRITICAL',
      message: 'Question count must be between 1 and 50.',
      recommendation: 'Adjust the number of questions'
    });
  }

  // MEDIUM: Warn if question types array is empty
  if (!builderState.questionTypes || builderState.questionTypes.length === 0) {
    issues.push({
      type: 'INVALID_ANSWER',
      severity: 'CRITICAL',
      message: 'No question types selected.',
      recommendation: 'Select at least one question type (MCQ, True/False, etc.)'
    });
  }

  // MEDIUM: Warn if large request (may timeout)
  if (builderState.count > 30) {
    issues.push({
      type: 'TOPIC_MISMATCH',
      severity: 'LOW',
      message: `Generating ${builderState.count} questions may take 30-60 seconds.`,
      recommendation: 'Consider generating in smaller batches for faster results'
    });
  }

  // LOW: Warn if subject-topic mismatch detected
  if (builderState.subject && builderState.title) {
    const detectedSubject = detectSubjectFromTopic(builderState.title);
    if (detectedSubject && detectedSubject !== builderState.subject && builderState.subject !== 'General') {
      issues.push({
        type: 'SUBJECT_MISMATCH',
        severity: 'LOW',
        message: `Topic "${builderState.title}" appears to be ${detectedSubject}, but ${builderState.subject} was selected.`,
        recommendation: `Consider changing subject to "${detectedSubject}" for better results, or continue with current settings`
      });
    }
  }

  // Calculate score
  const score = calculateValidationScore([], issues);

  return {
    passed: issues.filter(i => i.severity === 'CRITICAL').length === 0,
    score,
    issues
  };
}
